import { useState, useEffect, useMemo } from "react";
import ModalForm from "../../../../../components/ModalForm";
import AlertMessage, { AlertLevel } from "../../../../../components/AlertMessage";
import {
	useGetClassLinkSettingsInfoQuery,
	DistrictClassLinkInfoResult,
	useUpdateClassLinkSettingsInfoMutation,
} from "../../../districtsApiSlice";
import ClassLinkSettingsForm from "./ClassLinkSettingsForm";

export interface DistrictClassLinkInfoSectionProps {
	districtId: number;
	isOutsideSiteFormOpen: boolean;
	setIsOutsideSiteFormOpen: (value: boolean) => void;
	onChange: (patch: Partial<{ classLinkId: string }>) => void;
}

export default function DistrictClassLinkSettings({
	districtId,
	isOutsideSiteFormOpen,
	setIsOutsideSiteFormOpen,
	onChange,
}: DistrictClassLinkInfoSectionProps) {
	const [classLinkData, setClassLinkData] = useState<DistrictClassLinkInfoResult>();
	const [originalClassLinkData, setOriginalClassLinkData] =
		useState<DistrictClassLinkInfoResult>();
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState("");

	const { data: classLinkSettingsData, isLoading } = useGetClassLinkSettingsInfoQuery(districtId);

	const [updateClassLinkSettings] = useUpdateClassLinkSettingsInfoMutation();

	// Reset local state to server data when modal opens and store original data
	useEffect(() => {
		if (isOutsideSiteFormOpen && classLinkSettingsData) {
			setClassLinkData(classLinkSettingsData);
			setOriginalClassLinkData(classLinkSettingsData);
		}
	}, [isOutsideSiteFormOpen, classLinkSettingsData]);

	const hasChanges = useMemo(() => {
		const currentData = classLinkData;
		const originalData = originalClassLinkData;
		return JSON.stringify(currentData) !== JSON.stringify(originalData);
	}, [classLinkData, originalClassLinkData]);

	const { allFilled, allEmpty } = useMemo(() => {
		if (classLinkData) {
			const requiredFields = [
				classLinkData.tenantId,
				classLinkData.classLinkId,
				classLinkData.clientId,
				classLinkData.clientSecret,
				classLinkData.endpointUrl,
			];
			const allFilled = requiredFields.every((field) => !!field?.trim());
			const allEmpty = requiredFields.every((field) => !field?.trim());

			return { allFilled, allEmpty };
		}

		return { allFilled: false, allEmpty: false };
	}, [classLinkData]);

	const canSubmit = hasChanges && (allFilled || allEmpty) && !isSaving && !isLoading;

	const handleSubmit = () => {
		setIsSaving(true);
		if (classLinkData) {
			updateClassLinkSettings({
				districtId: districtId,
				tenantId: classLinkData.tenantId,
				classLinkID: classLinkData.classLinkId,
				clientId: classLinkData.clientId,
				clientSecret: classLinkData.clientSecret,
				endpointUrl: classLinkData.endpointUrl,
				syncStatus: classLinkData.syncStatus,
			})
				.unwrap()
				.then(() => {
					onChange({ classLinkId: classLinkData.classLinkId });
					onModalClosed();
				})
				.catch((error) => {
					setSaveError(error.data);
					setIsSaving(false);
				});
		}
	};

	const onModalClosed = () => {
		setIsOutsideSiteFormOpen(false);
		setIsSaving(false);
		setSaveError("");
	};

	return (
		<ModalForm
			open={isOutsideSiteFormOpen}
			title={"ClassLink Settings"}
			form={
				<>
					<AlertMessage
						className="my-2"
						type={AlertLevel.Error}
						message={saveError}
						showIcon={true}
						onClear={() => setSaveError("")}
					/>
					<ClassLinkSettingsForm
						data={classLinkData}
						setData={setClassLinkData}
						isLoading={isLoading}
					/>
				</>
			}
			confirmLabel="Save"
			disableConfirm={!canSubmit}
			onConfirm={handleSubmit}
			onCancel={onModalClosed}
		/>
	);
}
